import { ConfigurationError } from "./errors-CEDWDXew.js";
import { logIssue } from "./logging-B7X3yL0T.js";
import { ApiClientError } from "./errors-DG2FreDU.js";
import { ErrorEntrySchema, ThreadsEntrySchema } from "./schema-BYZYkgA1.js";
import { findMostRelevantInAppFrame } from "./code-location-DW_YNpMG.js";

//#region src/tools/support/code-location.ts
const CODE_LOCATION_TIMEOUT_MS = 3e3;
/** Resolves a verified source location, omitting optional enrichment failures. */
async function resolveCodeLocation({ apiService, organizationSlug, projectSlug, event }) {
	const frame = findRelevantFrame(event);
	if (!frame) return void 0;
	const embeddedSourceUrl = getTrustedGitHubSourceUrl(frame.sourceLink);
	if (embeddedSourceUrl) return {
		repository: getGitHubRepository(embeddedSourceUrl),
		path: getString(frame.filename) ?? getString(frame.absPath),
		...frame.lineNo !== null && frame.lineNo !== void 0 ? { line: frame.lineNo } : {},
		url: embeddedSourceUrl
	};
	const file = getString(frame.filename) ?? getString(frame.absPath);
	if (!file) return void 0;
	const controller = new AbortController();
	const timeout = setTimeout(() => controller.abort(), CODE_LOCATION_TIMEOUT_MS);
	try {
		const result = await apiService.getStacktraceLink({
			organizationSlug,
			projectSlug,
			file,
			platform: getString(frame.platform) ?? getString(event.platform),
			...frame.lineNo !== null && frame.lineNo !== void 0 ? { lineNo: frame.lineNo } : {},
			absPath: getString(frame.absPath),
			module: getString(frame.module),
			package: getString(frame.package),
			commitId: getString(event.release?.lastCommit?.id),
			groupId: getString(event.groupID),
			sdkName: getString(event.sdk?.name),
			signal: controller.signal
		});
		const sourceUrl = getHttpUrl(result.sourceUrl);
		const repository = getString(result.config?.repoName);
		const sourcePath = getString(result.sourcePath);
		if (!sourceUrl || !repository || !sourcePath) return void 0;
		return {
			repository,
			path: sourcePath,
			...frame.lineNo !== null && frame.lineNo !== void 0 ? { line: frame.lineNo } : {},
			url: sourceUrl
		};
	} catch (error) {
		if (!controller.signal.aborted && !(error instanceof ApiClientError) && !(error instanceof ConfigurationError)) logIssue(error, {
			loggerScope: [
				"tools",
				"get-issue-details",
				"code-location"
			],
			contexts: { request: {
				organizationSlug,
				projectSlug,
				groupId: event.groupID
			} }
		});
		return void 0;
	} finally {
		clearTimeout(timeout);
	}
}
function findRelevantFrame(event) {
	for (const entry of event.entries) {
		if (entry.type !== "exception") continue;
		const parsed = ErrorEntrySchema.safeParse(entry.data);
		if (!parsed.success) continue;
		const exceptions = parsed.data.values ?? (parsed.data.value ? [parsed.data.value] : []);
		const rootException = [...exceptions].reverse().find((exception) => exception?.stacktrace?.frames?.length);
		if (rootException?.stacktrace?.frames) return findMostRelevantInAppFrame(rootException.stacktrace.frames);
	}
	for (const entry of event.entries) {
		if (entry.type !== "threads") continue;
		const parsed = ThreadsEntrySchema.safeParse(entry.data);
		if (!parsed.success) continue;
		const crashedThread = parsed.data.values?.find((thread) => thread.crashed && thread.stacktrace?.frames?.length);
		if (crashedThread?.stacktrace?.frames) return findMostRelevantInAppFrame(crashedThread.stacktrace.frames);
	}
	return void 0;
}
function getHttpUrl(value) {
	const url = getString(value);
	if (!url) return void 0;
	try {
		const parsed = new URL(url);
		return parsed.protocol === "https:" || parsed.protocol === "http:" ? url : void 0;
	} catch {
		return void 0;
	}
}
function getTrustedGitHubSourceUrl(value) {
	const url = getHttpUrl(value);
	if (!url) return void 0;
	const parsed = new URL(url);
	return parsed.protocol === "https:" && (parsed.hostname === "github.com" || parsed.hostname === "www.github.com") ? url : void 0;
}
function getGitHubRepository(sourceUrl) {
	const [owner, repository] = new URL(sourceUrl).pathname.split("/").filter(Boolean);
	return owner && repository ? `${owner}/${repository}` : void 0;
}
function getString(value) {
	return typeof value === "string" && value.length > 0 ? value : void 0;
}

//#endregion
export { resolveCodeLocation };
//# sourceMappingURL=code-location-C0fIXD0V.js.map